import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/elephant/constants';
import { isDependentSearchFilter } from '@aibees/elephant/feature/search-filters';
import { useSearchRouteInfo, useTrackEvents } from '@aibees/elephant/hooks';
import { useSearchQueryFilter } from '@aibees/elephant/hooks/use-search-query-filter';
import { FILTER_CHANGE_PERF_MARK } from '@aibees/shared/constants/app';
import { classnames, peformanceLogTime } from '@aibees/shared/utils';
import { decodeQueryParamValues } from '@aibees/shared/utils/url';
import { Card, CardContent, Stack } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';
import type {
  EnrichedCheckboxOption,
  CheckListFilterWithOptionsProps as Props,
} from '.';
import { SearchFilterStatusWrapper } from '../../components/search-filter-status-wrapper/search-filter-status-wrapper';
import { SearchFilterOptionMetadata } from '../../types';
import { CheckboxItem } from './checkbox-item';
import * as SX from './sx';
import { useSelectAllItemState } from './use-select-all-item-state';
import {
  buildSearchParamsDraft,
  generateSearchMatchParamsKey,
  isCheckboxChecked,
  isCheckboxDisabled,
  isComingSoon,
} from './utils';

export const CheckListFilterWithOptions = memo(
  ({ className, config, options, field, metadata, open, nested }: Props) => {
    const { type, table } = useSearchRouteInfo();
    const { track } = useTrackEvents();

    const searchMatchParamsKey = useMemo(
      () => generateSearchMatchParamsKey(field ?? ''),
      [field]
    );

    // Build select keys so the hook only re-renders when relevant params change
    const selectKeys = useMemo(() => {
      const keys: string[] = [];
      if (field) keys.push(field);
      if (searchMatchParamsKey) keys.push(searchMatchParamsKey);
      if (metadata?.availability?.requires)
        keys.push(...metadata.availability.requires);
      return keys;
    }, [field, searchMatchParamsKey, metadata?.availability?.requires]);

    const { params, removeParam, setParam, setParams } = useSearchQueryFilter({
      select: selectKeys,
    });

    // Derive decoded values directly from params -- eliminates a second store subscription
    const values = useMemo(
      () => decodeQueryParamValues(params[field!]),
      [params, field]
    );

    const { selectAllCheckboxOption, handleSelectAllCheckboxClick } =
      useSelectAllItemState({
        params,
        field,
        options,
        metadata,
        setParams,
      });

    const handleSelectAllClickWithPerfMark = useCallback(() => {
      if (LOG_API_CALL_DURATION_ENABLE) {
        performance.mark(FILTER_CHANGE_PERF_MARK);
      }
      handleSelectAllCheckboxClick();
    }, [handleSelectAllCheckboxClick]);

    const includeMetadata = useMemo(
      () =>
        !!(
          open &&
          metadata?.metaDataApi?.enabled &&
          !metadata?.metaDataApi?.hideCount
        ),
      [open, metadata?.metaDataApi?.enabled, metadata?.metaDataApi?.hideCount]
    );

    const isFilterDisabled = useMemo(
      () => isDependentSearchFilter(metadata, params),
      [metadata, params]
    );

    // Memoized click handler
    const handleClick = useCallback(
      (
        value: string,
        checked: boolean,
        optionMetadata?: SearchFilterOptionMetadata
      ) => {
        if (LOG_API_CALL_DURATION_ENABLE) {
          peformanceLogTime(`filter change start: ${field}`);
          performance.mark(FILTER_CHANGE_PERF_MARK);
        }

        const draft = buildSearchParamsDraft(
          value,
          checked,
          values,
          options,
          metadata?.skipUrlParams,
          optionMetadata
        );

        track('FILTER', {
          filterValue: draft,
          filterName: field,
          searchType: type,
        });

        setParam(field!, draft);

        if (LOG_API_CALL_DURATION_ENABLE)
          peformanceLogTime(`filter change end: ${field}`);
      },
      [values, options, metadata?.skipUrlParams, track, field, type, setParam]
    );

    const handleOnlyClick = useCallback(
      (value: string) => {
        setParam(field!, value);
      },
      [field, setParam]
    );

    // Pre-process options with checked, disabled, comingSoon states.
    // Reuse previous option object when derived state unchanged so CheckboxItem memo can skip.
    const prevEnrichedByValueRef = useRef<Map<string, EnrichedCheckboxOption>>(
      new Map()
    );
    const enrichedOptions = useMemo(() => {
      const nextByValue = new Map<string, EnrichedCheckboxOption>();
      const result =
        options?.map((option) => {
          const checked = isCheckboxChecked(
            isFilterDisabled,
            values,
            option.value,
            option.selected
          );
          const disabled = isCheckboxDisabled(
            isFilterDisabled,
            values.length,
            checked,
            metadata?.availability?.required
          );
          const comingSoon = isComingSoon(
            option.value,
            metadata?.checkbox?.comingSoonKeys
          );
          const key = option.value;
          const prev = prevEnrichedByValueRef.current.get(key);
          if (
            prev &&
            prev.checked === checked &&
            prev.disabled === disabled &&
            prev.comingSoon === comingSoon
          ) {
            nextByValue.set(key, prev);
            return prev;
          }
          const enriched = {
            ...option,
            checked,
            disabled,
            comingSoon,
          } as EnrichedCheckboxOption;
          nextByValue.set(key, enriched);
          return enriched;
        }) ?? [];
      prevEnrichedByValueRef.current = nextByValue;
      return result;
    }, [
      options,
      values,
      isFilterDisabled,
      metadata?.availability?.required,
      metadata?.checkbox?.comingSoonKeys,
    ]);

    // Search-match param value to pass down to all CheckboxItems
    const searchMatchParamValue = params[searchMatchParamsKey];

    const content = (
      <Stack>
        {open && metadata?.checkbox?.showSelectAllOption && (
          <CheckboxItem
            searchMatchParamsKey={searchMatchParamsKey}
            searchMatchParamValue={searchMatchParamValue}
            onSetParam={setParam}
            onRemoveParam={removeParam}
            key="select-all"
            option={selectAllCheckboxOption}
            handleClick={handleSelectAllClickWithPerfMark}
            config={config}
            metadata={metadata}
            labelSx={SX.selectAllLabel}
          />
        )}
        {open &&
          enrichedOptions?.map((option) => (
            <CheckboxItem
              searchMatchParamsKey={searchMatchParamsKey}
              searchMatchParamValue={searchMatchParamValue}
              onSetParam={setParam}
              onRemoveParam={removeParam}
              key={option.value}
              option={option}
              handleClick={handleClick}
              config={config}
              metadata={metadata}
              table={table}
              includeMetadata={includeMetadata}
              isFilterDisabled={isFilterDisabled}
              handleOnlyClick={handleOnlyClick}
            />
          ))}
      </Stack>
    );

    return (
      <SearchFilterStatusWrapper
        className={classnames('InclusionCheckBoxFilter', className)}
      >
        {nested === false ? (
          <Card variant="filled" color="default">
            <CardContent padding="normal">{content}</CardContent>
          </Card>
        ) : (
          content
        )}
      </SearchFilterStatusWrapper>
    );
  },
  (prevProps, nextProps) =>
    prevProps.className === nextProps.className &&
    prevProps.field === nextProps.field &&
    prevProps.open === nextProps.open &&
    prevProps.config === nextProps.config &&
    prevProps.metadata === nextProps.metadata &&
    prevProps.options === nextProps.options &&
    prevProps.nested === nextProps.nested
);
